import { useCallback, useEffect, useRef, type Dispatch } from "react";
import type { ThemeAction } from "./themeState";

export function useIdleDetection(
  dispatch: Dispatch<ThemeAction>,
  onGoIdle: () => void,
  isLocked: boolean
) {
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onGoIdleRef = useRef(onGoIdle);
  onGoIdleRef.current = onGoIdle;

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    dispatch({ type: "ACTIVE" });

    if (isLocked) return;

    idleTimerRef.current = setTimeout(() => {
      dispatch({ type: "IDLE" });
      onGoIdleRef.current();
    }, 5000);
  }, [dispatch, isLocked]);

  // Stable event listeners
  useEffect(() => {
    const handler = () => resetIdleTimer();
    window.addEventListener("scroll", handler);
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("keydown", handler);
    };
  }, [resetIdleTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  return resetIdleTimer;
}
